import {
    genericWebBuilderBrief,
    squareOnlineStoreBrief,
    squareSpaceBrief,
} from '../../../shared/lib/platformUrls';

export type Features = {
    freeWebHost: boolean;
    shareIdeas: boolean;
    showcasePortfolio: boolean;
    basicOnlineStore: boolean;
    sellUnlimitedProduct: boolean;
    advancedEcommerceTool: boolean;
    sellB2B: boolean;
};

export interface WebBuilder {
    id: string;
    iconUrl: string;
    title: string;
    launchBriefUrl: string;
    color: string;
    blurb: string;
    image: string;
    isAvailableForLocale: (locale: string) => boolean;
    features: Features;
    pricing: {
        freeTrial: boolean;
        pricingStructure: 'both' | 'annually' | 'monthly';
    };
    support: {
        support247: boolean;
        liveChat: boolean;
        phone: boolean;
    };
}

const availableInAllLocales = () => true;

export function getAvailableWebBuilders(locale: string): WebBuilder[] {
    const webBuilders = getWebBuilders();
    return Object.keys(webBuilders).reduce((result, key) => {
        const builder = webBuilders[key];
        if (builder.isAvailableForLocale(locale)) {
            result.push(builder);
        }
        return result;
    }, [] as WebBuilder[]);
}

export function getWebBuilders(): { [key: string]: WebBuilder } {
    return {
        Wix: {
            id: 'wix',
            iconUrl: 'web-builder-selector/wix.png',
            title: 'Wix',
            launchBriefUrl: `${genericWebBuilderBrief()}?platform=wix`,
            color: '#0c6efc',
            blurb: __(
                'Wix has a large selection of templates and an easy to use, drag and drop, interface. You will need to upgrade to a paid account to remove their advertising.'
            ),
            image: 'logo-wix.png',
            isAvailableForLocale: availableInAllLocales,
            features: {
                freeWebHost: true,
                shareIdeas: true,
                showcasePortfolio: true,
                basicOnlineStore: true,
                sellUnlimitedProduct: false,
                advancedEcommerceTool: false,
                sellB2B: false,
            },
            pricing: {
                freeTrial: true,
                pricingStructure: 'annually',
            },
            support: {
                support247: true,
                liveChat: false,
                phone: true,
            },
        },
        Shopify: {
            id: 'shopify',
            iconUrl: 'web-builder-selector/shopify.png',
            title: 'Shopify',
            launchBriefUrl: `${genericWebBuilderBrief()}?platform=shopify`,
            color: '#000639',
            blurb: __(
                'Shopify is a popular ecommerce builder for selling a large range of products. They provide 24/7 phone support, and require a monthly subscription fee.'
            ),
            image: 'logo-shopify.png',
            isAvailableForLocale: availableInAllLocales,
            features: {
                freeWebHost: true,
                shareIdeas: false,
                showcasePortfolio: false,
                basicOnlineStore: true,
                sellUnlimitedProduct: true,
                advancedEcommerceTool: true,
                sellB2B: true,
            },
            pricing: {
                freeTrial: false,
                pricingStructure: 'monthly',
            },
            support: {
                support247: true,
                liveChat: true,
                phone: true,
            },
        },
        Jimdo: {
            id: 'jimdo',
            iconUrl: 'web-builder-selector/jimdo.png',
            title: 'Jimdo',
            launchBriefUrl: `${genericWebBuilderBrief()}?platform=jimdo`,
            color: '#00828c',
            blurb: __(
                "Jimdo has a selection of templates, with some modification available. It's good for personal and small businesses and blogs. Both a free and paid subscription are available."
            ),
            image: 'logo-jimdo.png',
            isAvailableForLocale: availableInAllLocales,
            features: {
                freeWebHost: true,
                shareIdeas: true,
                showcasePortfolio: true,
                basicOnlineStore: true,
                sellUnlimitedProduct: false,
                advancedEcommerceTool: false,
                sellB2B: false,
            },
            pricing: {
                freeTrial: true,
                pricingStructure: 'annually',
            },
            support: {
                support247: false,
                liveChat: false,
                phone: false,
            },
        },
        Squarespace: {
            id: 'squarespace',
            iconUrl: 'web-builder-selector/squarespace.png',
            title: 'Squarespace',
            launchBriefUrl: squareSpaceBrief(),
            color: '#000000',
            blurb: __(
                'Squarespace has a highly curated set of beautiful, polished templates. It has extensive styling options, and requires a monthly subscription fee.'
            ),
            image: 'logo-sqsp.png',
            isAvailableForLocale: availableInAllLocales,
            features: {
                freeWebHost: true,
                shareIdeas: true,
                showcasePortfolio: true,
                basicOnlineStore: true,
                sellUnlimitedProduct: true,
                advancedEcommerceTool: true,
                sellB2B: false,
            },
            pricing: {
                freeTrial: false,
                pricingStructure: 'both',
            },
            support: {
                support247: true,
                liveChat: true,
                phone: false,
            },
        },
        'Square Online Store': {
            id: 'square',
            iconUrl: 'web-builder-selector/square.png',
            title: 'Square Online Store',
            launchBriefUrl: squareOnlineStoreBrief(),
            color: '#1b2126',
            blurb: __(
                "Square Online Store is a new offering from Square. It's popular with cafes, restaurants, and other small businesses. It has a free plan where you incur a small % fee when you sell an item, or a paid plan."
            ),
            image: 'logo-square.png',
            isAvailableForLocale: (locale: string) => {
                const standardizedLocale = locale.toUpperCase();
                return (
                    ['US', 'CA', 'JP', 'AU', 'GB'].findIndex((region) =>
                        standardizedLocale.includes(region)
                    ) >= 0
                );
            },
            features: {
                freeWebHost: true,
                shareIdeas: false,
                showcasePortfolio: false,
                basicOnlineStore: true,
                sellUnlimitedProduct: true,
                advancedEcommerceTool: true,
                sellB2B: false,
            },
            pricing: {
                freeTrial: true,
                pricingStructure: 'annually',
            },
            support: {
                support247: true,
                liveChat: true,
                phone: true,
            },
        },
        Magento: {
            id: 'magento',
            iconUrl: 'web-builder-selector/magento.png',
            title: 'Magento',
            launchBriefUrl: `${genericWebBuilderBrief()}?platform=magento`,
            color: '#f16322',
            blurb: __(
                "Magento is an open-source platform for online shopping and inventory. It doesn't include product pages, blogs, or other marketing-type pages. Their basic offering is free, but you will need to pay an annual fee if you want to use this to its full extent."
            ),
            image: 'logo-magento.png',
            isAvailableForLocale: availableInAllLocales,
            features: {
                freeWebHost: false,
                shareIdeas: false,
                showcasePortfolio: false,
                basicOnlineStore: false,
                sellUnlimitedProduct: true,
                advancedEcommerceTool: true,
                sellB2B: true,
            },
            pricing: {
                freeTrial: false,
                pricingStructure: 'monthly',
            },
            support: {
                support247: true,
                liveChat: false,
                phone: true,
            },
        },
        Weebly: {
            id: 'weebly',
            iconUrl: 'web-builder-selector/weebly.png',
            title: 'Weebly',
            launchBriefUrl: `${genericWebBuilderBrief()}?platform=weebly`,
            color: '#3192e7',
            blurb: __(
                'Weebly has a smaller set of tools when compared to other web builders, but is very easy to use. You will need to upgrade to a paid account to remove their advertising.'
            ),
            image: 'logo-weebly.png',
            isAvailableForLocale: availableInAllLocales,
            features: {
                freeWebHost: true,
                shareIdeas: true,
                showcasePortfolio: true,
                basicOnlineStore: true,
                sellUnlimitedProduct: true,
                advancedEcommerceTool: true,
                sellB2B: false,
            },
            pricing: {
                freeTrial: true,
                pricingStructure: 'annually',
            },
            support: {
                support247: true,
                liveChat: true,
                phone: true,
            },
        },
    };
}
